import { View, Pressable, Image, Alert, Linking, StyleProp, ViewStyle } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption, SubHeading } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { TripStatusBadge } from '@/components/trips/TripStatusBadge';
import { useTheme } from '@/theme';
import type { Trip } from '@/services/trips';
import type { ItineraryItem, ItineraryBookingSnapshot } from '@/services/itinerary';
import type { Booking } from '@/services/bookings';
import type { TripAlert } from '@/utils/tripAlerts';
import type { DayBriefing, DayBriefingItem } from '@/services/dayBriefing';
import { FlightStatusCard } from '@/components/bookings/FlightStatusCard';
import { refreshFlightStatus, fetchCheckInLink, getFallbackCheckInUrl, updateNotificationPreferences as updateFlightNotificationPreferences } from '@/services/flightStatus';
import type { FlightNotificationPreferences } from '@/services/bookings';
import { useLandmarkImage } from '@/hooks/useLandmarkImage';
import { calculateDaysUntil } from '@/data/trips';
import { useEffect, useMemo, useState, memo } from 'react';
import { getTimezoneForLocation } from '@/services/timezones';
import type { LocationCoordinates } from '@/services/location';
import { humanizeAsyncError } from '@/utils/asyncErrorMessage';
import { logAnalyticsEvent } from '@/services/analyticsLogger';
import { resolveTripSurfaceTimezone, resolveItemStartInstant, toDateKeyInTimezone } from '@/utils/tripItemSchedule';
import { diffTripCalendarDateKeys } from '@/utils/tripCalendar';
import { formatTripItemScheduleLabel } from '@/utils/tripItemScheduleLabel';

type ActiveTripStatusCardProps = {
  trip: Trip;
  nextItem?: ItineraryItem | null;
  booking?: Booking | null;
  onOpenItinerary?: (tripId: string) => void;
  onShareTrip?: (trip: Trip) => void;
  onDismissCard?: (tripId: string) => void;
  onMinimizeCard?: (tripId: string) => void;
  showDismissControls?: boolean;
  alerts?: TripAlert[];
  briefing?: DayBriefing | null;
};

type ActiveTripDetailsProps = ActiveTripStatusCardProps & {
  variant?: 'card' | 'sheet';
  showActions?: boolean;
  onAddItem?: () => void;
  onShare?: () => void;
  alerts?: TripAlert[];
  briefing?: DayBriefing | null;
  style?: StyleProp<ViewStyle>;
};

const formatTime = (value?: string) => {
  if (!value) return null;
  const [hours, minutes] = value.split(':');
  const h = parseInt(hours, 10);
  if (Number.isNaN(h)) return null;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayHour = h % 12 || 12;
  return `${displayHour}:${minutes ?? '00'} ${ampm}`;
};

const formatDate = (date: Date) =>
  date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });

const formatDateRange = (startDate: Date, endDate: Date) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  if (startDate.getFullYear() !== new Date().getFullYear()) {
    return `${start}, ${startDate.getFullYear()} - ${end}, ${endDate.getFullYear()}`;
  }
  return `${start} - ${end}`;
};

const formatTripCalendarDateKey = (dateKey: string): string => {
  const [year, month, day] = dateKey.split('-').map((value) => Number.parseInt(value, 10));
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const getRelativeTimeLabel = (item: ItineraryItem, timeZone: string, trip: Trip): string | null => {
  const now = new Date();
  const event = resolveItemStartInstant(item, timeZone, trip);
  const diffMs = event.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = diffTripCalendarDateKeys(toDateKeyInTimezone(now, timeZone), toDateKeyInTimezone(event, timeZone));

  if (diffDays === 0) {
    if (diffHours < 0) {
      const hoursAgo = Math.abs(diffHours);
      return hoursAgo === 1 ? '1 hour ago' : `${hoursAgo} hours ago`;
    }
    if (diffHours === 0) {
      const minutesUntil = Math.floor(Math.abs(diffMs) / (1000 * 60));
      if (minutesUntil < 60) {
        return minutesUntil <= 0 ? 'Now' : `In ${minutesUntil} min`;
      }
    }
    if (diffHours > 0 && diffHours < 24) {
      return `In ${diffHours} ${diffHours === 1 ? 'hour' : 'hours'}`;
    }
    return 'Today';
  }
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1 && diffDays <= 7) return `In ${diffDays} days`;
  if (diffDays < -1 && diffDays >= -7) return `${Math.abs(diffDays)} days ago`;
  return null;
};

const SnapshotChip = ({
  icon,
  label,
  color,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  label: string;
  color: string;
}) => {
  const theme = useTheme();
  return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing(0.5),
          paddingHorizontal: theme.spacing(1.25),
          paddingVertical: theme.spacing(0.75),
          borderRadius: theme.radii.pill,
          backgroundColor: theme.colors.borderMuted + '60',
        }}
      >
      <Ionicons name={icon} size={16} color={color} />
      <Caption style={{ fontSize: 13, color, fontWeight: '500' }}>{label}</Caption>
    </View>
  );
};

const renderSnapshotChips = (snapshot?: ItineraryBookingSnapshot | null, theme?: ReturnType<typeof useTheme>) => {
  if (!snapshot || !theme) return null;
  const chips: { icon: keyof typeof Ionicons.glyphMap; label: string; color: string }[] = [];
  if (snapshot.status) {
    chips.push({
      icon: 'checkmark-circle-outline',
      label: snapshot.status.replace(/_/g, ' '),
      color: theme.colors.primary,
    });
  }
  if (snapshot.gate || snapshot.terminal) {
    const gateLabel = [snapshot.terminal, snapshot.gate].filter(Boolean).join(' ');
    chips.push({
      icon: 'airplane-outline',
      label: gateLabel,
      color: theme.colors.text,
    });
  }
  if (snapshot.confirmationNumber) {
    chips.push({
      icon: 'pricetag-outline',
      label: snapshot.confirmationNumber,
      color: theme.colors.text,
    });
  }

  if (chips.length === 0) return null;
  return (
    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(0.75), marginTop: theme.spacing(0.5) }}>
      {chips.map((chip, index) => (
        <SnapshotChip key={`${chip.label}-${index}`} {...chip} />
      ))}
    </View>
  );
};

const briefingIconForKind = (kind: DayBriefingItem['kind']): keyof typeof Ionicons.glyphMap => {
  switch (kind) {
    case 'weather':
      return 'partly-sunny-outline';
    case 'booking':
      return 'ticket-outline';
    case 'checklist':
      return 'checkbox-outline';
    case 'budget':
      return 'wallet-outline';
    case 'safety':
      return 'shield-checkmark-outline';
    case 'itinerary':
    default:
      return 'calendar-outline';
  }
};

const briefingColorForPriority = (
  priority: DayBriefingItem['priority'],
  theme: ReturnType<typeof useTheme>,
): string => {
  if (priority === 'high') return theme.colors.warning;
  if (priority === 'medium') return theme.colors.primary;
  return theme.colors.textSecondary;
};

export function ActiveTripDetails({
  trip,
  nextItem,
  booking,
  onOpenItinerary,
  variant = 'card',
  showActions = true,
  onAddItem,
  onShare,
  alerts = [],
  briefing,
  style,
}: ActiveTripDetailsProps) {
  const theme = useTheme();
  const router = useRouter();
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [updatingPrefs, setUpdatingPrefs] = useState(false);
  const [tripTimezone, setTripTimezone] = useState<string | null>(null);

  const timezoneCoordinates: LocationCoordinates | null = useMemo(() => {
    const primary =
      trip.primaryReferencePointId && trip.referencePoints?.length
        ? trip.referencePoints.find((p) => p.id === trip.primaryReferencePointId)?.coordinates
        : undefined;
    if (primary) return primary;

    const firstRef = trip.referencePoints?.[0]?.coordinates;
    if (firstRef) return firstRef;

    const firstDestination = trip.destinations?.find((d) => d.coordinates)?.coordinates;
    if (firstDestination) return firstDestination as LocationCoordinates;

    return null;
  }, [trip.primaryReferencePointId, trip.referencePoints, trip.destinations]);

  const timezoneKey = timezoneCoordinates
    ? `${timezoneCoordinates.latitude.toFixed(4)},${timezoneCoordinates.longitude.toFixed(4)}`
    : null;

  useEffect(() => {
    let cancelled = false;
    if (!timezoneCoordinates) {
      setTripTimezone(null);
      return;
    }
    getTimezoneForLocation(timezoneCoordinates)
      .then((tz) => {
        if (!cancelled) setTripTimezone(tz);
      })
      .catch(() => {
        if (!cancelled) setTripTimezone(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timezoneKey]);

  const handleOpen = () => {
    if (onOpenItinerary) {
      onOpenItinerary(trip.id);
      return;
    }
    router.push({
      pathname: '/(tabs)/plan',
      params: { tripId: trip.id },
    });
  };

  const handleAddItem = () => {
    if (onAddItem) {
      onAddItem();
      return;
    }
    router.push({
      pathname: '/(tabs)/plan',
      params: { tripId: trip.id, action: 'add' },
    });
  };

  const handleOpenCollaborators = () => {
    if (onShare) {
      onShare();
      return;
    }
  };

  const handleShare = () => {
    handleOpenCollaborators();
  };

  const handleAlertAction = (alert: TripAlert) => {
    if (alert.type === 'docs') {
      router.push({
        pathname: '/(tabs)/documents',
        params: { tripId: trip.id },
      });
      return;
    }
    if (alert.type === 'budget') {
      router.push({
        pathname: '/(tabs)/wallet',
        params: { tripId: trip.id },
      });
      return;
    }
    if (alert.type === 'collaborators') {
      handleShare();
      return;
    }
    handleOpen();
  };

  const handleRefreshFlightStatus = async () => {
    if (refreshingStatus || !booking?.tripId) return;
    try {
      setRefreshingStatus(true);
      await refreshFlightStatus(booking.id, booking.tripId);
    } catch (error: any) {
      console.error('Error refreshing flight status:', error);
      Alert.alert('Unable to refresh status', humanizeAsyncError(error, 'Please try again shortly.'));
    } finally {
      setRefreshingStatus(false);
    }
  };

  const handleOpenCheckIn = async () => {
    if (checkInLoading || !booking?.tripId) return;
    try {
      setCheckInLoading(true);
      let link: string | null = null;
      try {
        link = await fetchCheckInLink(booking.id, booking.tripId);
      } catch {
        link = null;
      }
      if (!link) {
        const code = booking.airlineCode ?? (booking.provider ? booking.provider.replace(/\d+/g, '').trim() : undefined);
        link = getFallbackCheckInUrl(code);
      }
      if (link) {
        await Linking.openURL(link);
      } else {
        Alert.alert('Check-in unavailable', 'We could not find an online check-in link. Try your airline’s website.');
      }
    } catch (error: any) {
      console.error('Error opening check-in link:', error);
      const fallback = getFallbackCheckInUrl(booking.airlineCode ?? (booking.provider ? booking.provider.replace(/\d+/g, '').trim() : undefined));
      if (fallback) {
        try {
          await Linking.openURL(fallback);
        } catch {
          Alert.alert('Unable to open check-in', 'Please try your airline’s website.');
        }
      } else {
        Alert.alert('Unable to open check-in', humanizeAsyncError(error, 'Please try your airline’s website.'));
      }
    } finally {
      setCheckInLoading(false);
    }
  };

  const handleUpdatePreferences = async (prefs: FlightNotificationPreferences) => {
    if (updatingPrefs || !booking?.tripId) return;
    try {
      setUpdatingPrefs(true);
      await updateFlightNotificationPreferences(booking.id, booking.tripId, prefs);
    } catch (error: any) {
      console.error('Error updating flight notification preferences:', error);
      Alert.alert('Update failed', humanizeAsyncError(error, 'Could not update preferences.'));
    } finally {
      setUpdatingPrefs(false);
    }
  };

  const { imageUrl } = useLandmarkImage(trip.destination, trip.landmarkImage ?? null);
  const daysUntil = calculateDaysUntil(trip.startDate);
  const activeNextItem = briefing?.nextItem ?? nextItem ?? null;
  const tripDayInfo = briefing?.dayContext.currentDay
    ? { current: briefing.dayContext.currentDay, total: briefing.dayContext.totalDays }
    : null;
  const tripProgress = briefing?.dayContext.progressPercent ?? 0;
  const scheduleTimeZone = tripTimezone ?? resolveTripSurfaceTimezone(trip);
  const relativeTimeLabel = activeNextItem
    ? getRelativeTimeLabel(activeNextItem, scheduleTimeZone, trip)
    : null;
  const nextItemScheduleLabel = activeNextItem
    ? formatTripItemScheduleLabel(activeNextItem, scheduleTimeZone, trip)
    : null;
  const isUrgent = activeNextItem && relativeTimeLabel && (relativeTimeLabel.includes('hour') || relativeTimeLabel === 'Now' || relativeTimeLabel === 'Today');

  const dayEssentialsLabel =
    briefing?.dayContext?.currentDay && briefing.dayContext.todayKey
      ? `Day ${briefing.dayContext.currentDay} · ${formatTripCalendarDateKey(briefing.dayContext.todayKey)}`
      : 'Today';

  const handleOpenDocs = () => {
    router.push({
      pathname: '/(tabs)/documents',
      params: { tripId: trip.id },
    });
  };

  const handleOpenBudget = () => {
    router.push({
      pathname: '/(tabs)/wallet',
      params: { tripId: trip.id },
    });
  };

  const handleOpenSafety = () => {
    router.push({
      pathname: '/(tabs)/travel',
      params: { tripId: trip.id, tab: 'safety' },
    });
  };

  const handleOpenMaps = () => {
    router.push({
      pathname: '/(tabs)/travel',
      params: { tripId: trip.id, tab: 'maps' },
    });
  };

  const handleBriefingAction = (item: DayBriefingItem) => {
    void logAnalyticsEvent('day_briefing_action_tap', {
      tripId: trip.id,
      itemKind: item.kind,
      itemId: item.id,
      priority: item.priority,
      surface: variant === 'sheet' ? 'bottom_sheet' : 'active_trip_card',
    });
    if (item.routeTarget === 'wallet' || item.kind === 'budget') {
      handleOpenBudget();
      return;
    }
    if (item.routeTarget === 'documents') {
      handleOpenDocs();
      return;
    }
    if (item.routeTarget === 'collaborators') {
      handleOpenCollaborators();
      return;
    }
    if (item.routeTarget === 'share') {
      handleShare();
      return;
    }
    if (item.kind === 'weather') {
      return;
    }
    if (item.routeTarget === 'safety' || item.kind === 'safety') {
      handleOpenSafety();
      return;
    }
    if (item.routeTarget === 'plan' || item.kind === 'checklist') {
      handleOpen();
      return;
    }
    if (item.kind === 'booking') {
      handleOpen();
      return;
    }
    handleOpen();
  };

  const handleQuickAddTemplate = (template: 'meal' | 'nap' | 'buffer' | 'restroom' | 'play') => {
    router.push({
      pathname: '/(tabs)/plan',
      params: {
        tripId: trip.id,
        action: 'add',
        template,
      },
    });
  };

  return (
    <View style={[{ gap: theme.spacing(2.5) }, style]}>
      {/* Header Section with Image */}
      <View style={{ flexDirection: 'row', gap: theme.spacing(2), alignItems: 'flex-start' }}>
        {/* Trip Image */}
        {imageUrl && (
          <View
            style={{
              width: 70,
              height: 70,
              borderRadius: theme.radii.md,
              overflow: 'hidden',
              backgroundColor: theme.colors.borderMuted,
            }}
          >
            <Image
              source={{ uri: imageUrl }}
              style={{ width: '100%', height: '100%' }}
              resizeMode="cover"
              accessibilityLabel={`Trip image for ${trip.destination || trip.name}`}
            />
          </View>
        )}

        {/* Trip Info */}
        <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 9, fontWeight: '400', textTransform: 'uppercase', letterSpacing: 0.5, opacity: 0.7 }}>
            Active trip
          </Caption>
          <SubHeading style={{ fontSize: 22, fontWeight: '700', lineHeight: 28 }}>{trip.name}</SubHeading>
          
          {/* Destination and Dates */}
          <View style={{ gap: theme.spacing(0.5) }}>
            {trip.destination && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                <Ionicons name="location-outline" size={14} color={theme.colors.textSecondary} />
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
                  {trip.destination}
                </Caption>
              </View>
            )}
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 13, fontWeight: '500' }}>
              {formatDateRange(trip.startDate, trip.endDate)}
            </Caption>

            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5), flexWrap: 'wrap' }}>
              {tripDayInfo && (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: theme.spacing(0.5),
                    paddingHorizontal: theme.spacing(1),
                    paddingVertical: theme.spacing(0.4),
                    borderRadius: theme.radii.pill,
                    backgroundColor: theme.colors.primary + '15',
                  }}
                >
                  <Ionicons name="time-outline" size={12} color={theme.colors.primary} />
                  <Caption style={{ fontSize: 11, fontWeight: '600', color: theme.colors.primary }}>
                    Day {tripDayInfo.current} of {tripDayInfo.total}
                  </Caption>
                </View>
              )}
              {daysUntil > 0 && (
                <View
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: theme.spacing(0.5),
                    paddingHorizontal: theme.spacing(1),
                    paddingVertical: theme.spacing(0.4),
                    borderRadius: theme.radii.pill,
                    backgroundColor: theme.colors.warning + '15',
                  }}
                >
                  <Ionicons name="calendar-outline" size={12} color={theme.colors.warning} />
                  <Caption style={{ fontSize: 11, fontWeight: '600', color: theme.colors.warning }}>
                    {daysUntil === 1 ? 'Starts tomorrow' : `Starts in ${daysUntil} days`}
                  </Caption>
                </View>
              )}
            </View>
          </View>
        </View>

        <TripStatusBadge startDate={trip.startDate} endDate={trip.endDate} size="medium" />
      </View>

      {/* Trip Progress Indicator */}
      {tripDayInfo && tripDayInfo.current > 0 && (
        <View style={{ gap: theme.spacing(0.5) }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
              Day {tripDayInfo.current} of {tripDayInfo.total}
            </Caption>
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
              {tripProgress}%
            </Caption>
          </View>
          <View
            style={{
              height: 6,
              backgroundColor: theme.colors.borderMuted,
              borderRadius: theme.radii.pill,
              overflow: 'hidden',
            }}
          >
            <View
              style={{
                height: '100%',
                width: `${tripProgress}%`,
                backgroundColor: theme.colors.primary,
              }}
            />
          </View>
        </View>
      )}

      {/* Needs attention */}
      {alerts && alerts.length > 0 && (
        <View
          style={{
            gap: theme.spacing(1),
            padding: theme.spacing(1.25),
            borderRadius: theme.radii.md,
            backgroundColor: theme.colors.warning + '10',
            borderWidth: 1,
            borderColor: theme.colors.warning + '40',
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
            <Ionicons name="alert-circle-outline" size={16} color={theme.colors.warning} />
            <Caption style={{ fontWeight: '700', color: theme.colors.warning }}>Needs attention</Caption>
          </View>
          <View style={{ gap: theme.spacing(0.5) }}>
            {alerts.slice(0, 2).map((alert) => (
              <Pressable
                key={alert.id}
                onPress={() => handleAlertAction(alert)}
                style={({ pressed }) => ({
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  gap: theme.spacing(0.75),
                  padding: theme.spacing(0.75),
                  borderRadius: theme.radii.sm,
                  backgroundColor: pressed ? theme.colors.warning + '12' : 'transparent',
                })}
                accessibilityRole="button"
                accessibilityLabel={`${alert.title}: ${alert.message}`}
                accessibilityHint={alert.actionLabel || 'Open to resolve this alert'}
              >
                <Ionicons name="information-circle-outline" size={16} color={theme.colors.warning} style={{ marginTop: 2 }} />
                <View style={{ flex: 1 }}>
                  <ThemedText variant="bodyStrong" style={{ fontSize: 14, fontWeight: '600' }}>
                    {alert.title}
                  </ThemedText>
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 12, marginTop: 2 }}>
                    {alert.message}
                  </Caption>
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, marginTop: 2, fontWeight: '600' }}>
                    {alert.actionLabel || 'Review in plan'}
                  </Caption>
                </View>
              </Pressable>
            ))}
            {alerts.length > 2 && (
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                +{alerts.length - 2} more
              </Caption>
            )}
          </View>
        </View>
      )}

      {/* Next Item Section */}
      {activeNextItem ? (
        <View
          style={{
            gap: theme.spacing(1),
            padding: theme.spacing(1.5),
            backgroundColor: isUrgent ? theme.colors.warning + '10' : theme.colors.borderMuted + '30',
            borderRadius: theme.radii.md,
            borderLeftWidth: isUrgent ? 3 : 0,
            borderLeftColor: isUrgent ? theme.colors.warning : 'transparent',
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: theme.spacing(0.5) }}>
            <ThemedText variant="bodyStrong" style={{ fontSize: 16, fontWeight: '600' }}>
              Next: {activeNextItem.title}
            </ThemedText>
            {relativeTimeLabel && (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: theme.spacing(0.5),
                  paddingHorizontal: theme.spacing(1),
                  paddingVertical: theme.spacing(0.4),
                  borderRadius: theme.radii.pill,
                  backgroundColor: isUrgent ? theme.colors.warning + '20' : theme.colors.primary + '15',
                }}
              >
                <Ionicons
                  name={isUrgent ? 'time' : 'calendar-outline'}
                  size={12}
                  color={isUrgent ? theme.colors.warning : theme.colors.primary}
                />
                <Caption
                  style={{
                    fontSize: 11,
                    fontWeight: '600',
                    color: isUrgent ? theme.colors.warning : theme.colors.primary,
                  }}
                >
                  {relativeTimeLabel}
                </Caption>
              </View>
            )}
          </View>
          
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flexWrap: 'wrap' }}>
            <ThemedText variant="bodyStrong" style={{ fontSize: 15, fontWeight: '600', color: theme.colors.text }}>
              {nextItemScheduleLabel ?? activeNextItem.title}
            </ThemedText>
          </View>

          {activeNextItem.location && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Ionicons name="pin-outline" size={14} color={theme.colors.textSecondary} />
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
                {activeNextItem.location}
              </Caption>
            </View>
          )}

          {renderSnapshotChips(activeNextItem.bookingSnapshot, theme)}

          {/* Flight Details */}
          {booking && booking.type === 'flight' && (
            <View style={{ marginTop: theme.spacing(1) }}>
              <FlightStatusCard
                booking={booking}
                onRefresh={handleRefreshFlightStatus}
                isRefreshing={refreshingStatus}
                onCheckIn={handleOpenCheckIn}
                isCheckInLoading={checkInLoading}
                onUpdatePreferences={handleUpdatePreferences}
                isUpdatingPreferences={updatingPrefs}
              />
            </View>
          )}
        </View>
      ) : (
        <View
          style={{
            gap: theme.spacing(1),
            padding: theme.spacing(1.5),
            backgroundColor: theme.colors.borderMuted + '20',
            borderRadius: theme.radii.md,
            alignItems: 'center',
          }}
        >
          {daysUntil > 0 ? (
            <>
              <ThemedText variant="bodyStrong" style={{ fontSize: 16, fontWeight: '600', textAlign: 'center' }}>
                Trip starts {formatDate(trip.startDate)}
              </ThemedText>
              <Body color={theme.colors.textSecondary} style={{ fontSize: 13, textAlign: 'center', marginTop: theme.spacing(0.5) }}>
                In {daysUntil} {daysUntil === 1 ? 'day' : 'days'} • Add activities to your itinerary
              </Body>
            </>
          ) : (
            <>
              <ThemedText variant="bodyStrong" style={{ fontSize: 16, fontWeight: '600', textAlign: 'center' }}>
                No upcoming events
              </ThemedText>
              <Body color={theme.colors.textSecondary} style={{ fontSize: 13, textAlign: 'center', marginTop: theme.spacing(0.5) }}>
                Keep your itinerary up to date to unlock proactive reminders
              </Body>
            </>
          )}
        </View>
      )}

      {/* Today essentials */}
      <View
        style={{
          gap: theme.spacing(1),
          padding: theme.spacing(1.25),
          borderRadius: theme.radii.md,
          backgroundColor: theme.colors.backgroundSecondary,
          borderWidth: 1,
          borderColor: theme.colors.borderMuted,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
          <Ionicons name="sparkles-outline" size={16} color={theme.colors.primary} />
          <Caption style={{ fontWeight: '700', color: theme.colors.text }}>
            {dayEssentialsLabel}
          </Caption>
        </View>
        {briefing?.items?.length ? (
          <View style={{ gap: theme.spacing(0.75) }}>
            {briefing.items.slice(0, 3).map((item) => {
              const color = briefingColorForPriority(item.priority, theme);
              const rowStyle = {
                flexDirection: 'row' as const,
                alignItems: 'flex-start' as const,
                gap: theme.spacing(0.75),
                padding: theme.spacing(0.85),
                borderRadius: theme.radii.sm,
                backgroundColor: theme.colors.surface,
                borderWidth: 1,
                borderColor: item.priority === 'high' ? theme.colors.warning + '35' : theme.colors.borderMuted,
              };
              const rowContent = (
                <>
                  <Ionicons name={briefingIconForKind(item.kind)} size={16} color={color} style={{ marginTop: 2 }} />
                  <View style={{ flex: 1 }}>
                    <ThemedText variant="bodyStrong" style={{ fontSize: 13, fontWeight: '700', color: theme.colors.text }}>
                      {item.title}
                    </ThemedText>
                    {item.detail ? (
                      <Caption color={theme.colors.textSecondary} style={{ fontSize: 11, marginTop: 2 }}>
                        {item.detail}
                      </Caption>
                    ) : null}
                  </View>
                  {item.actionLabel ? (
                    <Caption style={{ fontSize: 11, fontWeight: '700', color }}>
                      {item.actionLabel}
                    </Caption>
                  ) : null}
                </>
              );

              if (item.kind === 'weather') {
                return (
                  <View
                    key={item.id}
                    style={rowStyle}
                    accessibilityRole="text"
                    accessibilityLabel={`${item.title}${item.detail ? `. ${item.detail}` : ''}`}
                  >
                    {rowContent}
                  </View>
                );
              }

              return (
                <Pressable
                  key={item.id}
                  onPress={(event) => {
                    event.stopPropagation?.();
                    handleBriefingAction(item);
                  }}
                  style={({ pressed }) => ({
                    ...rowStyle,
                    backgroundColor: pressed ? theme.colors.primary + '12' : theme.colors.surface,
                  })}
                  accessibilityRole="button"
                  accessibilityLabel={`${item.title}${item.detail ? `. ${item.detail}` : ''}`}
                >
                  {rowContent}
                </Pressable>
              );
            })}
          </View>
        ) : null}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) }}>
          <Pressable
            onPress={handleOpenDocs}
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.spacing(0.5),
              paddingHorizontal: theme.spacing(1.25),
              paddingVertical: theme.spacing(0.75),
              borderRadius: theme.radii.pill,
              backgroundColor: pressed ? theme.colors.primary + '15' : theme.colors.surface,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
            })}
          >
            <Ionicons name="document-text-outline" size={16} color={theme.colors.primary} />
            <Caption style={{ fontSize: 12, fontWeight: '600', color: theme.colors.text }}>Docs</Caption>
          </Pressable>
          <Pressable
            onPress={handleOpenBudget}
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.spacing(0.5),
              paddingHorizontal: theme.spacing(1.25),
              paddingVertical: theme.spacing(0.75),
              borderRadius: theme.radii.pill,
              backgroundColor: pressed ? theme.colors.primary + '15' : theme.colors.surface,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
            })}
          >
            <Ionicons name="wallet-outline" size={16} color={theme.colors.primary} />
            <Caption style={{ fontSize: 12, fontWeight: '600', color: theme.colors.text }}>Budget</Caption>
          </Pressable>
          <Pressable
            onPress={handleOpenSafety}
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.spacing(0.5),
              paddingHorizontal: theme.spacing(1.25),
              paddingVertical: theme.spacing(0.75),
              borderRadius: theme.radii.pill,
              backgroundColor: pressed ? theme.colors.primary + '15' : theme.colors.surface,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
            })}
          >
            <Ionicons name="shield-checkmark-outline" size={16} color={theme.colors.primary} />
            <Caption style={{ fontSize: 12, fontWeight: '600', color: theme.colors.text }}>Safety</Caption>
          </Pressable>
          <Pressable
            onPress={handleOpenMaps}
            style={({ pressed }) => ({
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.spacing(0.5),
              paddingHorizontal: theme.spacing(1.25),
              paddingVertical: theme.spacing(0.75),
              borderRadius: theme.radii.pill,
              backgroundColor: pressed ? theme.colors.primary + '15' : theme.colors.surface,
              borderWidth: 1,
              borderColor: theme.colors.borderMuted,
            })}
          >
            <Ionicons name="map-outline" size={16} color={theme.colors.primary} />
            <Caption style={{ fontSize: 12, fontWeight: '600', color: theme.colors.text }}>Maps</Caption>
          </Pressable>
        </View>
      </View>

      {/* Family quick-add */}
      <View
        style={{
          gap: theme.spacing(1),
          padding: theme.spacing(1.25),
          borderRadius: theme.radii.md,
          backgroundColor: theme.colors.backgroundSecondary,
          borderWidth: 1,
          borderColor: theme.colors.borderMuted,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5), justifyContent: 'space-between' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
            <Ionicons name="sparkles-outline" size={16} color={theme.colors.primary} />
            <Caption style={{ fontWeight: '700', color: theme.colors.text }}>
              Family quick-add
            </Caption>
          </View>
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 11 }}>
            Adds to today’s plan
          </Caption>
        </View>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) }}>
          {[
            { label: 'Meal', icon: 'restaurant-outline', template: 'meal' as const },
            { label: 'Nap', icon: 'moon-outline', template: 'nap' as const },
            { label: 'Buffer', icon: 'hourglass-outline', template: 'buffer' as const },
            { label: 'Restroom', icon: 'water-outline', template: 'restroom' as const },
          ].map((quick) => (
            <Pressable
              key={quick.template}
              onPress={() => handleQuickAddTemplate(quick.template)}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                paddingHorizontal: theme.spacing(1.25),
                paddingVertical: theme.spacing(0.75),
                borderRadius: theme.radii.pill,
                backgroundColor: pressed ? theme.colors.primary + '15' : theme.colors.surface,
                borderWidth: 1,
                borderColor: theme.colors.borderMuted,
              })}
            >
              <Ionicons name={quick.icon as any} size={16} color={theme.colors.primary} />
              <Caption style={{ fontSize: 12, fontWeight: '600', color: theme.colors.text }}>{quick.label}</Caption>
            </Pressable>
          ))}
        </View>
      </View>

      {variant === 'card' && showActions && (
        <View style={{ flexDirection: 'row', gap: theme.spacing(1), flexWrap: 'wrap' }}>
          <Button
            label="Open plan"
            onPress={handleOpen}
            variant="primary"
            icon={<Ionicons name="calendar-outline" size={16} color={theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF'} />}
            style={{ flex: 1 }}
            accessibilityLabel="Open plan"
            accessibilityHint="Opens the full plan for this trip"
          />
          <Pressable
            onPress={(e) => {
              e.stopPropagation();
              handleAddItem();
            }}
            style={({ pressed }) => [
              {
                paddingHorizontal: theme.spacing(2),
                paddingVertical: theme.spacing(1.25),
                borderRadius: theme.radii.md,
                backgroundColor: theme.colors.primary + '15',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: pressed ? 0.7 : 1,
                minWidth: 44,
                minHeight: 44,
                flexDirection: 'row',
                gap: theme.spacing(0.5),
              },
            ]}
            accessibilityRole="button"
            accessibilityLabel="Add item"
            accessibilityHint="Adds a new item to the itinerary"
          >
            <Ionicons name="add" size={18} color={theme.colors.primary} />
            <ThemedText variant="bodyStrong" style={{ color: theme.colors.primary, fontSize: 14, fontWeight: '600' }}>
              Add item
            </ThemedText>
          </Pressable>
          {onShare && (
            <Pressable
              onPress={(e) => {
                e.stopPropagation();
                handleShare();
              }}
              style={({ pressed }) => [
                {
                  paddingHorizontal: theme.spacing(1.75),
                  paddingVertical: theme.spacing(1.1),
                  borderRadius: theme.radii.md,
                  backgroundColor: theme.colors.borderMuted + '35',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: pressed ? 0.7 : 1,
                  minWidth: 44,
                  minHeight: 44,
                  flexDirection: 'row',
                  gap: theme.spacing(0.5),
                },
              ]}
              accessibilityRole="button"
              accessibilityLabel="Share trip"
              accessibilityHint="Open sharing and collaboration options for this trip"
            >
              <Ionicons name="share-social-outline" size={18} color={theme.colors.text} />
              <ThemedText variant="bodyStrong" style={{ color: theme.colors.text, fontSize: 14, fontWeight: '600' }}>
                Share
              </ThemedText>
            </Pressable>
          )}
        </View>
      )}
    </View>
  );
}

function ActiveTripStatusCardComponent({
  trip,
  nextItem,
  booking,
  onOpenItinerary,
  onShareTrip,
  onDismissCard,
  onMinimizeCard,
  showDismissControls = false,
  alerts = [],
  briefing,
}: ActiveTripStatusCardProps) {
  const router = useRouter();
  const theme = useTheme();

  const handleOpen = (tripId: string) => {
    if (onOpenItinerary) {
      onOpenItinerary(tripId);
      return;
    }
    router.push({
      pathname: '/(tabs)/plan',
      params: { tripId },
    });
  };

  return (
    <View>
      <Card variant="elevated" elevation={2} style={{ overflow: 'hidden' }}>
          {showDismissControls && (
            <View
              style={{
                position: 'absolute',
                top: theme.spacing(1),
                right: theme.spacing(1),
                flexDirection: 'row',
                gap: theme.spacing(0.5),
                zIndex: 2,
              }}
              pointerEvents="box-none"
            >
              {onMinimizeCard && (
                <Pressable
                  onPress={(event) => {
                    event.stopPropagation();
                    onMinimizeCard(trip.id);
                  }}
                  style={({ pressed }) => ({
                    padding: theme.spacing(0.75),
                    borderRadius: theme.radii.pill,
                    backgroundColor: theme.colors.surface + 'aa',
                    opacity: pressed ? 0.7 : 1,
                  })}
                  accessibilityRole="button"
                  accessibilityLabel="Minimize active trip card"
                >
                  <Ionicons name="remove-outline" size={18} color={theme.colors.text} />
                </Pressable>
              )}
              {onDismissCard && (
                <Pressable
                  onPress={(event) => {
                    event.stopPropagation();
                    onDismissCard(trip.id);
                  }}
                  style={({ pressed }) => ({
                    padding: theme.spacing(0.75),
                    borderRadius: theme.radii.pill,
                    backgroundColor: theme.colors.surface + 'aa',
                    opacity: pressed ? 0.7 : 1,
                  })}
                  accessibilityRole="button"
                  accessibilityLabel="Close active trip card"
                >
                  <Ionicons name="close" size={18} color={theme.colors.text} />
                </Pressable>
              )}
            </View>
          )}
          <ActiveTripDetails
            trip={trip}
            nextItem={nextItem}
            booking={booking}
            onOpenItinerary={handleOpen}
            onShare={onShareTrip ? () => onShareTrip(trip) : undefined}
            alerts={alerts}
            briefing={briefing}
            style={showDismissControls ? { paddingTop: theme.spacing(1.5) } : undefined}
          />
      </Card>
    </View>
  );
}

// Memoized component for performance
export const ActiveTripStatusCard = memo(ActiveTripStatusCardComponent, (prevProps, nextProps) => {
  return (
    prevProps.trip.id === nextProps.trip.id &&
    prevProps.trip.name === nextProps.trip.name &&
    prevProps.trip.startDate.getTime() === nextProps.trip.startDate.getTime() &&
    prevProps.nextItem?.id === nextProps.nextItem?.id &&
    prevProps.booking?.id === nextProps.booking?.id &&
    prevProps.alerts?.length === nextProps.alerts?.length &&
    prevProps.briefing?.trayLine === nextProps.briefing?.trayLine &&
    prevProps.briefing?.items.length === nextProps.briefing?.items.length &&
    prevProps.onShareTrip === nextProps.onShareTrip &&
    prevProps.showDismissControls === nextProps.showDismissControls &&
    prevProps.onDismissCard === nextProps.onDismissCard &&
    prevProps.onMinimizeCard === nextProps.onMinimizeCard
  );
});
